import { useCallback, useEffect, useState } from "react";

import useAxios from "@hooks/use-axios";
import { CURRENCY } from "@src/utility/constants/panel/common";
import { EARNINGS_DEFAULT_LIMIT, EARNINGS_ENDPOINTS, PAID_STATUS } from "../misc/constants";
import { normalizeInvoiceDocument } from "@panel/shared/invoice/misc/helpers";

const usePublisherEarnings = (publisherId) => {
    const { axiosInstance, loading } = useAxios({ base: "affiliate", initialLoader: true });
    const [rows, setRows] = useState([]);

    useEffect(() => {
        if (!publisherId) return;

        const fetchEarnings = async () => {
            try {
                const res = await axiosInstance.get(EARNINGS_ENDPOINTS.monthlyBreakdown(publisherId), { params: { page: 1, limit: EARNINGS_DEFAULT_LIMIT } });
                const result = res.data || res;
                const items = result.items || result.data || result.results || [];

                setRows(items.map((item, index) => ({
                    id               : `${item.invoice_number}-${index}`,
                    // The invoice's own id — what the document PATCH is
                    // addressed to. Rows without one cannot take an upload.
                    invoiceId        : item.id || item._id || item.invoice_id || "",
                    period           : item.period,
                    brand_name       : item.brand,
                    invoice_ref      : item.invoice_number,
                    amount           : item.amount,
                    status           : item.status,
                    isPaid           : item.status === PAID_STATUS,
                    // The publisher's tax invoice, when one is already attached.
                    document         : normalizeInvoiceDocument(item),
                    confirmation_id  : item.confirmation?.reference,
                    confirmation_date: item.confirmation?.paid_at
                })));
            } catch {
                setRows([]);
            }
        };
        fetchEarnings();
    }, [publisherId]); // eslint-disable-line react-hooks/exhaustive-deps

    // Patches the one row the upload was for rather than refetching the page —
    // the PATCH response already carries the stored document back.
    const applyInvoiceDocument = useCallback((rowId, document) => {
        setRows((current) => current.map((row) => (row.id === rowId ? { ...row, document } : row)));
    }, []);

    return { loading, currency: CURRENCY, rows, applyInvoiceDocument };
};

export default usePublisherEarnings;
